import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  Building,
  Copy,
  CheckCircle,
  AlertCircle,
  CreditCard,
  Check,
} from "lucide-react";
import { useErrorHandler } from "../../../hooks/useErrorHandler";
import { CompetitionPricesAPI } from "../../../APIs/competition_prices.api";
import { Loading } from "../../../components/loading";
import { ErrorMessage } from "../../../components/error-message";
import { PaymentsAPI } from "../../../APIs/payments.api";
import { useToast } from "../../../components/toast";
import { EditableField } from "../../../components/editable-field";
import { Button } from "../../../components/button";
import { SplitCUITField } from "../../../components/cuit-field";

export interface TransferPriceDetails {
  price_id: number;
  price: number;
  competition_id: number;
  payment_type: "transfer";
  transfer_account_id: number;
  transfer_alias: string;
  cvu_cbu?: string;
  bank_name: string;
}

interface TransferFormData {
  sender_first_name: string;
  sender_last_name: string;
  sender_cuit: string;
}

export function TransferPaymentView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [transferDetails, setTransferDetails] =
    useState<TransferPriceDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { error, handleError, clearError } = useErrorHandler();

  const { addToast } = useToast();

  const [formData, setFormData] = useState<TransferFormData>({
    sender_first_name: "",
    sender_last_name: "",
    sender_cuit: "",
  });

  useEffect(() => {
    if (!id) {
      navigate("/dashboard/competitions");
      return;
    }

    const fetchTransferDetails = async () => {
      clearError();
      setIsLoading(true);
      try {
        const response = await CompetitionPricesAPI.getTransferDetails(
          parseInt(id)
        );
        if (response.statusCode === 200) {
          setTransferDetails(response.data);
        } else {
          handleError(response.message, { persist: true });
        }
      } catch (err) {
        handleError("Error de conexión, intente nuevamente.", {
          persist: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransferDetails();
  }, [id, handleError, clearError, navigate]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.sender_first_name.trim() ||
      !formData.sender_last_name.trim() ||
      !formData.sender_cuit.trim()
    ) {
      handleError("Todos los campos son obligatorios", { persist: true });
      return;
    }

    if (!transferDetails?.price_id) {
      handleError("Error: No se encontraron los detalles de la transferencia", {
        persist: true,
      });
      return;
    }

    setIsSubmitting(true);
    clearError();

    const submitData = {
      comp_price_id: transferDetails.price_id,
      sender_first_name: formData.sender_first_name.trim(),
      sender_last_name: formData.sender_last_name.trim(),
      sender_cuit: formData.sender_cuit.trim(),
    };

    try {
      const response = await PaymentsAPI.createTransferPayment(submitData);
      if (response.statusCode === 201) {
        addToast({
          message: "Pago de transferencia creado exitosamente",
          type: "success",
          location: "bottom-center",
        });

        navigate("/dashboard/competitions");
      } else {
        handleError(response.message, { persist: true });
      }
    } catch (error) {
      handleError("Error al crear el pago de transferencia", { persist: true });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!transferDetails) {
    return (
      <div className="w-full p-8">
        <div className="text-center">
          <p className="text-gray-500">
            Detalles de transferencia no encontrados
          </p>
          <button
            onClick={() => navigate("/dashboard/competitions")}
            className="mt-4 text-blue-600 hover:text-blue-800"
          >
            Volver a competencias
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <button
          onClick={() => navigate("/dashboard/competitions")}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver a competencias
        </button>

        <div className="flex items-center">
          <CreditCard className="h-8 w-8 text-orange-600 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Pago por Transferencia
            </h1>
            <p className="text-gray-600">
              Completa tu inscripción realizando la transferencia
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Datos para la Transferencia
          </h2>

          <div className="space-y-4">
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-6 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="bg-orange-100 rounded-full p-2">
                    <AlertCircle className="h-6 w-6 text-orange-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-semibold text-orange-800">
                      Importante
                    </h3>
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-200 text-orange-800">
                      Instrucciones
                    </span>
                  </div>
                  <div className="text-orange-700 space-y-2">
                    <p className="font-medium">
                      Para completar tu inscripción:
                    </p>
                    <ol className="list-decimal list-inside space-y-1 text-sm">
                      <li>
                        Realiza la transferencia por el monto exacto a la cuenta
                        detallada abajo
                      </li>
                      <li>
                        Completa el formulario con los datos del remitente
                      </li>
                      <li>Aguarda la confirmación de la transferencia</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Monto a transferir
                </label>
                <div className="text-2xl font-bold text-green-600">
                  {formatPrice(transferDetails.price)}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Alias
                </label>
                <div className="flex items-center justify-between bg-white border rounded-lg p-3">
                  <span className="font-mono text-lg">
                    {transferDetails.transfer_alias}
                  </span>
                  <button
                    onClick={() =>
                      copyToClipboard(transferDetails.transfer_alias, "alias")
                    }
                    className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                  >
                    {copiedField === "alias" ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {transferDetails.cvu_cbu && (
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    CVU/CBU
                  </label>
                  <div className="flex items-center justify-between bg-white border rounded-lg p-3">
                    <span className="font-mono">{transferDetails.cvu_cbu}</span>
                    <button
                      onClick={() =>
                        copyToClipboard(transferDetails.cvu_cbu!, "cvu_cbu")
                      }
                      className="ml-2 p-1 text-gray-500 hover:text-gray-700"
                    >
                      {copiedField === "cvu_cbu" ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center">
                <Building className="h-4 w-4 text-gray-400 mr-2" />
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Banco
                  </label>
                  <div className="text-gray-900">
                    {transferDetails.bank_name}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Datos del Remitente
          </h2>

          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-700">
              <strong>Importante:</strong> Completa los datos de la persona que
              realizó la transferencia (puede ser diferente al titular de la
              cuenta de inscripción).
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <EditableField
              label="Nombre del Remitente"
              value={formData.sender_first_name}
              onChange={handleInputChange}
              placeholder="Nombre de quien hizo la transferencia"
              required
              name="sender_first_name"
            />

            <EditableField
              label="Apellido del Remitente"
              value={formData.sender_last_name}
              onChange={handleInputChange}
              placeholder="Apellido de quien hizo la transferencia"
              required
              name="sender_last_name"
            />

            <SplitCUITField
              label="CUIT del Remitente"
              value={formData.sender_cuit}
              onChange={handleInputChange}
              required
              name="sender_cuit"
            />

            <div className="flex justify-end">
              <Button
                type="submit"
                text={
                  isSubmitting ? "Procesando..." : "Confirmar Transferencia"
                }
                variant="create"
                disabled={isSubmitting}
                icon={<Check className="w-4 h-4 mr-2" />}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
